/**
 * This Tab displays as part of the User Preferences Modal that can be opened via the Header as well as from the setting gear in the AI Assistant.
 * It allows users to configure their AI Assistant preferences including:
 * - display settings (font size and answer verbosity)
 * - manage AI-generated memories (approve/reject pending memories and edit/delete saved memories)
 * - view privacy information about how their data is handled by the AI system.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  removeAIAssistantMemory,
  setAIAssistantFontSize,
  setAIAssistantGenerateMemory,
  setAIAssistantPreferredSubsection,
  setAIAssistantSavedMemories,
} from '@/aiAssistant/aiAssistant.actions';
import {
  approvePendingMemory,
  deletePendingMemory,
  deleteUserMemory,
  getAllMemories,
  memoryEventDispatcher,
  updateUserMemory,
} from '@/aiAssistant/aiAssistant.utilities';
import { sqAiAssistantStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { errorToast } from '@/utilities/toast.utilities';
import { Button, Checkbox, Icon, LoadingIndicator, TextArea } from '@seeqdev/qomponents';
import type { AIMemory } from '@/aiAssistant/aiAssistant.types';

type AiSection = 'general' | 'memories' | 'privacy';

export const AiPreferencesTab = () => {
  const { t } = useTranslation();

  const fontSize = useFluxPath(sqAiAssistantStore, () => sqAiAssistantStore.fontSize);
  const generateMemory = useFluxPath(sqAiAssistantStore, () => sqAiAssistantStore.generateMemory);
  const savedMemories = useFluxPath(sqAiAssistantStore, () => sqAiAssistantStore.savedMemories);
  const preferredSubsection = useFluxPath(sqAiAssistantStore, () => sqAiAssistantStore.preferredSubsection);

  // Initialize activeSection with preferredSubsection if it exists, otherwise default to 'general'
  const [activeSection, setActiveSection] = useState<AiSection>(preferredSubsection || 'general');
  const [showSavedMemories, setShowSavedMemories] = useState(true);
  const [showPendingMemories, setShowPendingMemories] = useState(true);
  const [isLoadingMemories, setIsLoadingMemories] = useState(false);
  const [pendingMemories, setPendingMemories] = useState<AIMemory[]>([]);

  // Set initial section based on preferred subsection from store
  useEffect(() => {
    if (preferredSubsection) {
      setActiveSection(preferredSubsection);
      // Clear the preferred subsection after using it
      setAIAssistantPreferredSubsection(null);
    }
  }, [preferredSubsection]);

  // Fetch memories when component mounts or when memories section is accessed
  useEffect(() => {
    if (activeSection === 'memories') {
      loadAllMemories();
    }
  }, [activeSection]);

  const loadAllMemories = async () => {
    setIsLoadingMemories(true);
    try {
      const memoriesResponse = await getAllMemories();
      setAIAssistantSavedMemories(memoriesResponse.approved);
      setPendingMemories(memoriesResponse.pending);
    } catch (error) {
      errorToast({
        messageKey: 'AI_ASSISTANT.ERROR.LOADING_MEMORIES',
      });
    } finally {
      setIsLoadingMemories(false);
    }
  };
  const handleApprovePendingMemory = async (memoryId: string) => {
    const success = await approvePendingMemory(memoryId);
    if (success) {
      // Remove from pending and reload all memories
      setPendingMemories((prev) => prev.filter((m) => m.id !== memoryId));
      await loadAllMemories();
      memoryEventDispatcher.notifyApproved(memoryId);
    }
  };

  const handleRemovePendingMemory = async (memoryId: string) => {
    const success = await deletePendingMemory(memoryId);
    if (success) {
      setPendingMemories((prev) => prev.filter((m) => m.id !== memoryId));
      memoryEventDispatcher.notifyRejected(memoryId);
    }
  };

  const handleRemoveMemory = async (memoryId: string) => {
    const success = await deleteUserMemory(memoryId);
    if (success) {
      removeAIAssistantMemory(memoryId);
      memoryEventDispatcher.notifyDeleted(memoryId);
    }
  };

  const handleUpdateMemory = async (memoryId: string, title: string, content: string): Promise<boolean> => {
    const success = await updateUserMemory(memoryId, title, content);
    if (success) {
      // Reload memories to get the updated timestamp from backend
      await loadAllMemories();
    }
    // returning because we want to stay in edit mode if the update fails
    return success;
  };

  const sections = [
    {
      id: 'general' as AiSection,
      label: t('AI_PREFERENCES.GENERAL.TITLE'),
      icon: 'fa-sparkles',
    },
    {
      id: 'memories' as AiSection,
      label: t('AI_PREFERENCES.MEMORIES.TITLE'),
      icon: 'fc-genai-chat',
    },
    {
      id: 'privacy' as AiSection,
      label: t('AI_PREFERENCES.PRIVACY.TITLE'),
      icon: 'fa-lock',
    },
  ];

  const SidebarSection = ({
    section,
    isActive,
    onClick,
  }: {
    section: { id: AiSection; label: string; icon: string };
    isActive: boolean;
    onClick: () => void;
  }) => (
    <Button
      variant="no-border"
      onClick={onClick}
      testId={`ai-section-${section.id}`}
      extraClassNames={`flexColumnContainer flexAlignCenter width-full cursorPointer p12 mb4 borderRadius transition-all tw:text-left ${
        isActive ? 'sq-bg-primary-light sq-text-primary' : 'sq-bg-transparent hover:sq-bg-gray-100'
      }`}
      label={
        <>
          <div className="flexShrink0 pr12">
            <Icon
              icon={section.icon}
              type={isActive ? 'theme' : 'color'}
              color={isActive ? undefined : '#6b7280'}
              testId={`ai-section-${section.id}-icon`}
            />
          </div>
          <div className="flexFill">{section.label}</div>
        </>
      }
    />
  );

  const SettingRow = ({
    label,
    description,
    children,
  }: {
    label: string;
    description?: string;
    children: React.ReactNode;
  }) => (
    <div className="mb20 pb15 borderBottom">
      <div className="flexRowContainer flexSpaceBetween flexAlignStart">
        <div className="flexFill pr15">
          <div className="text-bold">{label}</div>
          {description && <div className="tw:text-sq-text-muted mb10">{description}</div>}
        </div>
        <div className="flexShrink0">{children}</div>
      </div>
    </div>
  );

  const renderGeneralSection = () => (
    <div>
      <h4 className="mb20 mt0 text-bold">{t('AI_PREFERENCES.GENERAL.TITLE')}</h4>

      <SettingRow label={t('AI_ASSISTANT.SIZE.TITLE')} description={t('AI_PREFERENCES.GENERAL.TEXT_SIZE_DESC')}>
        <div className="flexRowContainer gap5">
          <Checkbox
            type="radio"
            id="smallSize"
            label={t('AI_ASSISTANT.SIZE.SMALL')}
            checked={fontSize === 'small'}
            onChange={() => setAIAssistantFontSize('small')}
          />
          <Checkbox
            type="radio"
            id="mediumSize"
            label={t('AI_ASSISTANT.SIZE.MEDIUM')}
            checked={fontSize === 'medium'}
            onChange={() => setAIAssistantFontSize('medium')}
          />
          <Checkbox
            type="radio"
            id="largeSize"
            label={t('AI_ASSISTANT.SIZE.LARGE')}
            checked={fontSize === 'large'}
            onChange={() => setAIAssistantFontSize('large')}
          />
        </div>
      </SettingRow>
    </div>
  );

  const MemoryCard = ({
    memory,
    onRemove,
    onUpdate,
  }: {
    memory: AIMemory;
    onRemove: (memoryId: string) => void;
    onUpdate: (memoryId: string, title: string, content: string) => Promise<boolean>;
  }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editContent, setEditContent] = useState(memory.content);

    const handleSave = async () => {
      const success = await onUpdate(memory.id, '', editContent); // Empty string for title since it's no longer used
      if (success) {
        setIsEditing(false);
      }
      // If update fails, stay in edit mode with current content so user can try again or cancel
    };

    const handleCancel = () => {
      setEditContent(memory.content);
      setIsEditing(false);
    };

    return (
      <div className="lightGreyBorder border-radius-8 p12 mb8 sq-bg-white">
        <div className="displayFlex flexAlignCenter mb8">
          {isEditing ? (
            <div className="flexFill mr8">
              <TextArea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                extraClassNames="width-100percent font-size-small p8 tw:border tw:rounded-2"
                rows={3}
                placeholder={t('AI_PREFERENCES.MEMORIES.PLACEHOLDERS.CONTENT')}
              />
            </div>
          ) : (
            <div className="flexFill displayFlex flexAlignCenter font-size-small">{memory.content}</div>
          )}
          <div className="displayFlex gap4 flexShrink0">
            {isEditing ? (
              <>
                <Button
                  variant="no-border"
                  icon="fa-check"
                  iconStyle="success"
                  testId={`save-memory-${memory.id}`}
                  onClick={handleSave}
                  extraClassNames="flexShrink0"
                  tooltip={t('AI_PREFERENCES.MEMORIES.TOOLTIPS.SAVE_MEMORY')}
                />
                <Button
                  variant="no-border"
                  icon="fa-times"
                  iconStyle="danger"
                  testId={`cancel-memory-${memory.id}`}
                  onClick={handleCancel}
                  extraClassNames="flexShrink0"
                  tooltip={t('AI_PREFERENCES.MEMORIES.TOOLTIPS.CANCEL_EDIT')}
                />
              </>
            ) : (
              <>
                <Button
                  variant="no-border"
                  icon="fa-pencil"
                  testId={`edit-memory-${memory.id}`}
                  onClick={() => setIsEditing(true)}
                  extraClassNames="flexShrink0"
                  tooltip={t('AI_PREFERENCES.MEMORIES.TOOLTIPS.EDIT_MEMORY')}
                />
                <Button
                  variant="no-border"
                  icon="fa-times"
                  iconStyle="danger"
                  testId={`remove-memory-${memory.id}`}
                  onClick={() => onRemove(memory.id)}
                  extraClassNames="flexShrink0"
                  tooltip={t('AI_PREFERENCES.MEMORIES.TOOLTIPS.DELETE_MEMORY')}
                />
              </>
            )}
          </div>
        </div>
        <div className="font-size-smaller tw:text-sq-text-muted mt8">
          {new Date(memory.updated_at).toLocaleString()}
        </div>
      </div>
    );
  };

  const PendingMemoryCard = ({
    memory,
    onApprove,
    onReject,
  }: {
    memory: AIMemory;
    onApprove: (memoryId: string) => void;
    onReject: (memoryId: string) => void;
  }) => {
    // Check if this is a memory update by seeing if the ID exists in approved memories
    // Use the current savedMemories state which is updated after any edits via loadAllMemories()
    const isMemoryUpdate = savedMemories.some((approvedMemory: AIMemory) => approvedMemory.id === memory.id);
    const originalMemory = isMemoryUpdate
      ? savedMemories.find((approvedMemory: AIMemory) => approvedMemory.id === memory.id)
      : null;
    const [isExpanded, setIsExpanded] = useState(false);

    // Additional validation: only show as update if content is actually different
    // This handles the edge case where a user manually edits an approved memory
    // while a pending update exists - we use the backend data as source of truth
    const hasContentChanged = originalMemory && originalMemory.content !== memory.content;
    const isActualUpdate = isMemoryUpdate && hasContentChanged;

    return (
      <div
        className={`lightGreyBorder border-radius-8 p12 mb8 ${isActualUpdate ? 'sq-bg-blue-50' : 'sq-bg-yellow-50'}`}>
        <div className="displayFlex flexAlignCenter mb8">
          <div className="flexFill">
            <div className="displayFlex flexAlignCenter flexSpaceBetween mb4">
              <div className="displayFlex flexAlignCenter">
                <div className="font-size-small font-semibold">
                  {isActualUpdate
                    ? t('AI_PREFERENCES.MEMORIES.MEMORY_UPDATE_PENDING')
                    : t('AI_PREFERENCES.MEMORIES.NEW_MEMORY_PENDING')}
                </div>
                {isActualUpdate && (
                  <button
                    className="ml8 p2 border-none sq-bg-transparent cursor-pointer"
                    onClick={() => setIsExpanded(!isExpanded)}>
                    <Icon icon={isExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} type="text" small={true} />
                  </button>
                )}
              </div>
              <div className="displayFlex gap4 flexShrink0">
                <Button
                  variant="no-border"
                  icon="fa-check"
                  testId={`approve-pending-memory-${memory.id}`}
                  onClick={() => onApprove(memory.id)}
                  iconStyle="success"
                  extraClassNames="flexShrink0"
                  tooltip={
                    isActualUpdate
                      ? t('AI_PREFERENCES.MEMORIES.APPROVE_UPDATE')
                      : t('AI_PREFERENCES.MEMORIES.APPROVE_MEMORY')
                  }
                />
                <Button
                  variant="no-border"
                  icon="fa-times"
                  testId={`reject-pending-memory-${memory.id}`}
                  onClick={() => onReject(memory.id)}
                  iconStyle="danger"
                  extraClassNames="flexShrink0"
                  tooltip={
                    isActualUpdate
                      ? t('AI_PREFERENCES.MEMORIES.DISCARD_UPDATE')
                      : t('AI_PREFERENCES.MEMORIES.DISCARD_MEMORY')
                  }
                />
              </div>
            </div>

            {/* Show content preview when collapsed */}
            {!isExpanded && <div className="text-small tw:text-sq-text-muted line-clamp-2">{memory.content}</div>}

            {/* Show detailed comparison when expanded */}
            {isExpanded && (
              <div className="mt12">
                {isActualUpdate && originalMemory ? (
                  <div>
                    {/* Original content - use current approved memory content from backend */}
                    <div className="mb12">
                      <div className="displayFlex flexAlignCenter mb4">
                        <Icon icon="fa-minus-circle" type="text" small={true} extraClassNames="mr4 sq-text-danger" />
                        <div className="text-xs tw:text-sq-text-muted font-semibold">
                          {t('AI_PREFERENCES.MEMORIES.ORIGINAL_CONTENT')}
                        </div>
                      </div>
                      <div className="sq-bg-red-50 border-left-4 border-red-300 p8 border-radius-4 text-small">
                        {originalMemory.content}
                      </div>
                    </div>

                    {/* Updated content */}
                    <div className="mb12">
                      <div className="displayFlex flexAlignCenter mb4">
                        <Icon icon="fa-plus-circle" type="text" small={true} extraClassNames="mr4 sq-text-success" />
                        <div className="text-xs tw:text-sq-text-muted font-semibold">
                          {t('AI_PREFERENCES.MEMORIES.UPDATED_CONTENT')}
                        </div>
                      </div>
                      <div className="sq-bg-green-50 border-left-4 border-green-300 p8 border-radius-4 text-small">
                        {memory.content}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-small">{memory.content}</div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="text-xs tw:text-sq-text-muted mt8">{new Date(memory.updated_at).toLocaleString()}</div>
      </div>
    );
  };

  const renderMemoriesSection = () => (
    <div>
      <h4 className="mt0 text-bold">{t('AI_PREFERENCES.MEMORIES.TITLE')}</h4>
      <h5 className="mb20 mt0 tw:text-sq-text-muted">{t('AI_PREFERENCES.MEMORIES.GENERATE_MEMORIES_DESC')}</h5>
      {/* Generate Memories Toggle */}
      <Checkbox
        id="generateMemory"
        label={t('AI_PREFERENCES.MEMORIES.ENABLE_MEMORIES')}
        checked={generateMemory}
        onChange={() => setAIAssistantGenerateMemory(!generateMemory)}
        extraClassNames="mb20 mt20"
      />

      {/* Pending Memories Section */}
      <div className="mb20 pb15 borderBottom">
        <div className="flexColumnContainer flexSpaceBetween flexAlignStart mb15">
          <div className="flexFill">
            <div className="displayFlex flexAlignCenter">
              <div className="displayFlex flexAlignCenter">
                <div className="text-bold">{t('AI_PREFERENCES.MEMORIES.PENDING_MEMORIES')}</div>
                <Button
                  variant="no-border"
                  icon="fa-circle-info"
                  iconStyle="text"
                  size="sm"
                  extraClassNames="pl4 pr4"
                  tooltip={t('AI_PREFERENCES.MEMORIES.MEMORY_LIMITS_PENDING')}
                />
              </div>
              {/* Show count and update indicator */}
              {pendingMemories.length > 0 &&
                (() => {
                  const updates = pendingMemories.filter((memory: AIMemory) => {
                    const approvedMemory = savedMemories.find((approved: AIMemory) => approved.id === memory.id);
                    return approvedMemory && approvedMemory.content !== memory.content;
                  }).length;
                  const newMemories = pendingMemories.length - updates;

                  return (
                    <div className="ml2 displayFlex gap4">
                      {newMemories > 0 && (
                        <span className="sq-bg-yellow-100 sq-text-yellow-800 text-xs px6 py2 border-radius-12">
                          {newMemories} {t('AI_PREFERENCES.MEMORIES.NEW_COUNT')}
                        </span>
                      )}
                      {updates > 0 && (
                        <span className="sq-bg-blue-100 sq-text-blue-800 text-xs px6 py2 border-radius-12">
                          {updates}{' '}
                          {updates === 1
                            ? t('AI_PREFERENCES.MEMORIES.UPDATE_COUNT_1')
                            : t('AI_PREFERENCES.MEMORIES.UPDATE_COUNT')}
                        </span>
                      )}
                    </div>
                  );
                })()}
            </div>
            <div className="tw:text-sq-text-muted">{t('AI_PREFERENCES.MEMORIES.PENDING_MEMORIES_DESC')}</div>
          </div>
          <Button
            variant="no-border"
            icon={showPendingMemories ? 'fa-chevron-up' : 'fa-chevron-down'}
            iconStyle="text"
            size="sm"
            testId="toggle-pending-memories"
            onClick={() => setShowPendingMemories(!showPendingMemories)}
            extraClassNames="expand-button ai-button"
          />
        </div>

        {/* Collapsible Content */}
        {showPendingMemories && (
          <div className="mt15">
            {(() => {
              if (pendingMemories.length > 0) {
                return (
                  <div>
                    {pendingMemories.map((memory) => (
                      <PendingMemoryCard
                        key={memory.id}
                        memory={memory}
                        onApprove={handleApprovePendingMemory}
                        onReject={handleRemovePendingMemory}
                      />
                    ))}
                  </div>
                );
              }
              return (
                <div className="tw:text-sq-text-muted py20">
                  <div className="text-small displayFlex flexAlignCenter">
                    {t('AI_PREFERENCES.MEMORIES.NO_PENDING_MEMORIES')}
                    {/* Reserved space for loading spinner, so it doesn't jump around when loading */}
                    <div className="ml8 width-16 height-16 displayFlex flexAlignCenter flexJustifyCenter">
                      {isLoadingMemories && <LoadingIndicator size="xs" />}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Saved Memories Section */}
      <div className="mb20 pb15">
        <div className="flexColumnContainer flexSpaceBetween flexAlignStart mb15">
          <div className="flexFill">
            <div className="displayFlex flexAlignCenter">
              <div className="text-bold">{t('AI_PREFERENCES.MEMORIES.SAVED_MEMORIES')}</div>
              <Button
                variant="no-border"
                icon="fa-circle-info"
                iconStyle="text"
                size="sm"
                extraClassNames="pl4 pr4"
                tooltip={t('AI_PREFERENCES.MEMORIES.MEMORY_LIMITS_SAVED')}
              />
            </div>
            <div className="tw:text-sq-text-muted">{t('AI_PREFERENCES.MEMORIES.SAVED_MEMORIES_DESC')}</div>
          </div>
          <Button
            variant="no-border"
            icon={showSavedMemories ? 'fa-chevron-up' : 'fa-chevron-down'}
            iconStyle="text"
            size="sm"
            testId="toggle-saved-memories"
            onClick={() => setShowSavedMemories(!showSavedMemories)}
            extraClassNames="expand-button ai-button"
          />
        </div>

        {/* Collapsible Content */}
        {showSavedMemories && (
          <div className="mt15">
            {(() => {
              if (savedMemories.length > 0) {
                return (
                  <div>
                    {savedMemories.map((memory: AIMemory) => (
                      <MemoryCard
                        key={memory.id}
                        memory={memory}
                        onRemove={handleRemoveMemory}
                        onUpdate={handleUpdateMemory}
                      />
                    ))}
                  </div>
                );
              }
              return (
                <div className="tw:text-sq-text-muted py20">
                  <div className="text-small displayFlex flexAlignCenter">
                    {t('AI_PREFERENCES.MEMORIES.NO_MEMORIES_DESC')}
                    {/* Reserved space for loading spinner, so it doesn't jump around when loading */}
                    <div className="ml8 width-16 height-16 displayFlex flexAlignCenter flexJustifyCenter">
                      {isLoadingMemories && <LoadingIndicator size="xs" />}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );

  const InformationSection = () => (
    <div className="mt20">
      {/* About Section */}
      <div className="mb30">
        <h5 className="text-bold mb10">{t('AI_ASSISTANT.INFORMATION.ABOUT')}</h5>
        <div className="tw:text-sq-text-muted mb10">{t('AI_ASSISTANT.INFORMATION.ABOUT_TEXT_1')}</div>
        <div className="tw:text-sq-text-muted">{t('AI_ASSISTANT.INFORMATION.ABOUT_TEXT_2')}</div>
      </div>

      {/* Data Privacy Section */}
      <div className="mb30">
        <h5 className="text-bold mb10">{t('AI_ASSISTANT.INFORMATION.DATA_PRIVACY')}</h5>
        <div className="tw:text-sq-text-muted mb10">{t('AI_ASSISTANT.INFORMATION.DATA_PRIVACY_TEXT_1')}</div>
        <div className="tw:text-sq-text-muted mb8">{t('AI_ASSISTANT.INFORMATION.DATA_PRIVACY_TEXT_2')}</div>
        <div className="tw:text-sq-text-muted mb4 ml15">
          {'• '}
          {t('AI_ASSISTANT.INFORMATION.DATA_PRIVACY_TEXT_3')}
        </div>
        <div className="tw:text-sq-text-muted ml15">
          {'• '}
          {t('AI_ASSISTANT.INFORMATION.DATA_PRIVACY_TEXT_4')}
        </div>
      </div>

      {/* Data Ownership Section */}
      <div className="mb30">
        <h5 className="text-bold mb10">{t('AI_ASSISTANT.INFORMATION.DATA_OWNERSHIP')}</h5>
        <div className="tw:text-sq-text-muted">{t('AI_ASSISTANT.INFORMATION.DATA_OWNERSHIP_TEXT')}</div>
      </div>

      {/* Security Section */}
      <div className="mb30">
        <h5 className="text-bold mb10">{t('AI_ASSISTANT.INFORMATION.SECURITY')}</h5>
        <div className="tw:text-sq-text-muted">{t('AI_ASSISTANT.INFORMATION.SECURITY_TEXT')}</div>
      </div>

      {/* Abuse Analysis Section */}
      <div className="mb30">
        <h5 className="text-bold mb10">{t('AI_ASSISTANT.INFORMATION.ABUSE')}</h5>
        <div className="tw:text-sq-text-muted">{t('AI_ASSISTANT.INFORMATION.ABUSE_TEXT')}</div>
      </div>
    </div>
  );

  const renderPrivacySection = () => (
    <div>
      <h4 className="mb20 mt0 text-bold">{t('AI_PREFERENCES.PRIVACY.TITLE')}</h4>
      <InformationSection />
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'general':
        return renderGeneralSection();
      case 'memories':
        return renderMemoriesSection();
      case 'privacy':
        return renderPrivacySection();
      default:
        return renderGeneralSection();
    }
  };

  return (
    <div className="flexColumnContainer height-maximum" data-testid="aiPreferencesTab">
      {/* Left Sidebar - Vertical Navigation */}
      <div className="flexRowContainer width-240 lightGreyBorderRight flexShrink0 pr0">
        <div className="flexRowContainer p16 sq-bg-gray-50 borderRadius height-fit">
          <nav className="flexRowContainer">
            {sections.map((section) => (
              <SidebarSection
                key={section.id}
                section={section}
                isActive={activeSection === section.id}
                onClick={() => setActiveSection(section.id)}
              />
            ))}
          </nav>
        </div>
      </div>

      {/* Right Content Area */}
      <div className="flexFill overflowYAuto pl20">
        <div className="pr20">{renderContent()}</div>
      </div>
    </div>
  );
};
